import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { initializeAmplify } from './auth/intitalizeCognito'
import { redirectToRememberedPath } from './auth/postAuthRedirect';
import { Auth, Hub } from "aws-amplify";
import TracerServiceClient from "./clients/tracerServiceClient";
import { apiHost, groundingGenerationApiHost, mmirUIIssueReportingApiHost, textToAkgEntityLinkingApiHost } from './config/apiConfig';
import { getDiscoverVideosRequestWithGroundingBody, getDiscoverImagesV2RequestWithGroundingBody, getDiscoverImagesV2RequestBody, getDiscoverVideosRequestBody, VIDEO_PROFILES, DEFAULT_VIDEO_PROFILE, IMAGE_PROFILES, DEFAULT_IMAGE_PROFILE, IMAGE_LOCALES, DEFAULT_IMAGE_LOCALE } from "./helpers/exampleDiscoverImagesV2RequestBody";
import LLMAnswerGenerationClient from './helpers/llmAnswerGeneration';
import { Menu, Home, BarChart, ExternalLink, Search, Calendar, Tag, Globe, Hash, ChevronDown, ChevronRight, ChevronUp, Save, X, Edit3, Settings, Anchor, Brain, Network } from 'lucide-react';
import ReactMarkdown from "react-markdown";
import ReactPlayer from "react-player";
import { formatDate, getImageCreationDate, isProdNA, openUrlInNewTab, suggestedQueries, isPartOfBetaUserGroup, matchesConfidenceBin, CONFIDENCE_BINS } from './helpers/appUtils';
import { getStage, getRegion } from './helpers/appUtils.js';
import SearchResultIssueReporter from './components/SearchResultIssueReporter.js';
import IssueReportingClient from './helpers/issueReportingClient.js';
import TextToAkgEntityLinkingClient from './helpers/textToAkgEntityLinkingClient.js';

const SENSITIVE_CONTENT_POLICY_ERROR_MESSAGE = "Request rejected due to sensitive content policy violation. Please check input and retry!";


const TextWithEntities = ({text, getEntityInfo, wordBoundaries, setWordBoundaries, scrollToEntity}) => {

  useEffect(() => {
    if (text && getEntityInfo) {
      getEntityInfo(text).then(setWordBoundaries).catch(() => setWordBoundaries([]));
    }
  }, [text, getEntityInfo]);

  return <HighlightEntitiesInText text={text} wordBoundaries={wordBoundaries} scrollToEntity={scrollToEntity} />;
};

const HighlightEntitiesInText = ({text, wordBoundaries, scrollToEntity}) => {
  const createSegments = () => {
    if (!wordBoundaries || wordBoundaries.length === 0) {
      return [{ text, isWord: false }];
    }

    const segments = [];
    let lastEnd = 0;

    // Sort boundaries by start position
    const filteredBoundaries = wordBoundaries.filter( wb => wb.info && wb.info !== '');
    const sortedBoundaries = [...filteredBoundaries].sort((a, b) => a.start - b.start);

    sortedBoundaries.forEach(({ start, end, info }, index) => {
      // Add text before the word
      if (start > lastEnd) {
        segments.push({
          text: text.slice(lastEnd, start),
          isWord: false
        });
      }

      // Add the word with entity index
      if (info && info !== '') {
        segments.push({
          text: text.slice(start, end),
          isWord: true,
          info: info,
          entityIndex: index
        });
      }

      lastEnd = end;
    });

    // Add remaining text
    if (lastEnd < text.length) {
      segments.push({
        text: text.slice(lastEnd),
        isWord: false
      });
    }

    return segments;
  };

  const segments = createSegments();

  return (
      <div>
        <div className="text-base leading-relaxed">
          {segments.map((segment, index) => (
            <span key={index}>
              {segment.isWord ? (
                <span
                  className="underline decoration-2 decoration-blue-500 cursor-pointer hover:decoration-blue-700 hover:bg-blue-50 transition-all duration-200 px-1 py-0.5 rounded"
                  onClick={() => scrollToEntity(segment.entityIndex)}
                  title="Click to view entity details below"
                >
                  {segment.text}
                </span>
              ) : (
                segment.text
              )}
            </span>
          ))}
        </div>
      </div>
  );
}

const EntityInfoList = ({ 
  text, 
  wordBoundaries,
  expandedEntities,
  toggleEntity
}) => {

  const entities = wordBoundaries.filter(wb => wb.info && wb.info !== '').map((boundary, index) => ({
    ...boundary,
    index,
    word: text.slice(boundary.start, boundary.end),
    info: boundary.info
  }));
                          
  return (
    <div className="divide-y divide-gray-100">
      {entities.filter((entity) => entity.info && entity.info !== '').map((entity) => (
        <div key={entity.index} id={`entity-${entity.index}`} className="transition-colors duration-200">
          <div
            className="flex items-center p-2 justify-between cursor-pointer hover:bg-gray-500"
            onClick={() => toggleEntity(entity.index)}
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {expandedEntities.has(entity.index) ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </div>
              <div>
                <span className="text-sm text-gray-400">
                  {entity.word}
                </span>
              </div>
            </div>
          </div>

          {expandedEntities.has(entity.index) && (
            <div className="px-4 pb-4 ml-7 border-blue-100">
              <div className="rounded-lg space-y-3">
                {typeof entity.info === 'string' && entity.info && (
                  <div className="break-all text-sm font-medium">
                  {entity.info.split('\n').map(line => <ReactMarkdown children={line} />)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CollapsibleSidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [activeItem, setActiveItem] = useState('');
  const history = useHistory();

  const menuItems = [
    { id: 'devHome', label: 'Developer Home', icon: Home , navigateTo: '/tracer'},
    { id: 'newTracer', label: 'New MMIR tracer', icon: ExternalLink, navigateTo: 'https://mmirtracer.harmony.a2z.com/' },
    { id: 'metrics', label: 'MMIR Dashboard', icon: BarChart, navigateTo: 'https://insights.ideas.agi.amazon.dev/multimodal-information-retrieval' }
  ];

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleItemClick = (item) => {
    setActiveItem(item.id);
    if (item.navigateTo) {
      history.push(item.navigateTo);
    }
  };

  return (
    <div className="flex bg-gray-100">
      {/* Sidebar */}
      <div
        className={`bg-slate-900 text-white transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-18' : 'w-64'
        } flex flex-col shadow-lg`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 my-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors duration-200"
          >
            {isCollapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                
                <li key={item.id}>
                  <a
                    href={item.navigateTo}
                    aria-label={item.label}
                    title={item.label}
                    className={`w-full flex items-center p-2 rounded-lg transition-all duration-200 ${
                      activeItem === item.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'hover:bg-slate-800 text-gray-300'
                    }`}
                  >
                    <IconComponent size={20} className="flex-shrink-0" aria-hidden="true" />
                    <span
                      className={`ml-3 font-medium ${
                        isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'
                      } transition-all duration-300 overflow-hidden whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

    </div>
  );
};

const ImageFilterComponent = (props) => {
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleDropdown = (filterName) => {
    setActiveDropdown(activeDropdown === filterName ? null : filterName);
  };

  const selectOption = (filterName, option) => {
    console.log(filterName);
    console.log(option);
    props.setFilters(prev => ({
      ...prev,
      [filterName]: option === 'All' || option === 'Any time' 
        ? filterName.charAt(0).toUpperCase() + filterName.slice(1).replace(/([A-Z])/g, ' $1').toLowerCase()
        : option
    }));
    setActiveDropdown(null);
  };

  const FilterDropdown = ({ name, label, options }) => (
    <div className="relative">
      <button
        onClick={() => toggleDropdown(name)}
        className="flex items-center space-x-1 px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200 text-sm"
      >
        <span>{props.filters[name]}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      
      {activeDropdown === name && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-48">
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => selectOption(name, option)}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full bg-gray-800 border-b border-gray-700">
      <div className="flex w-full py-0">
        <div className="flex justify-end w-full mx-40">
          <FilterDropdown name="profile" label="Profile" options={props.filterOptions.profile} />
          <FilterDropdown name="locale" label="Locale" options={props.filterOptions.locale} />
          <FilterDropdown name="confidenceBin" label="Confidence" options={props.filterOptions.confidenceBin} />
          <FilterDropdown name="size" label="Size" options={props.filterOptions.size} />
          <FilterDropdown name="sourceType" label="Source type" options={props.filterOptions.sourceType} />
          <FilterDropdown name="imageType" label="Image type" options={props.filterOptions.imageType} />
          <FilterDropdown name="time" label="Any time" options={props.filterOptions.time} />
        </div>
      </div>
      
      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </div>
  );
};

const VideoFilterComponent = (props) => {
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleDropdown = (filterName) => {
    setActiveDropdown(activeDropdown === filterName ? null : filterName);
  };

  const selectOption = (filterName, option) => {
    props.setFilters(prev => ({ ...prev, [filterName]: option }));
    setActiveDropdown(null);
  };

  const FilterDropdown = ({ name, label, options }) => (
    <div className="relative">
      <button
        onClick={() => toggleDropdown(name)}
        className="flex items-center space-x-1 px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200 text-sm"
      >
        <span>{label}: {props.filters[name]}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {activeDropdown === name && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-48">
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => selectOption(name, option)}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full bg-gray-800 border-b border-gray-700">
      <div className="flex w-full py-0">
        <div className="flex items-center justify-end w-full mx-40">
          <FilterDropdown name="profile" label="Profile" options={props.filterOptions.profile} />
        </div>
      </div>

      {activeDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </div>
  );
};

const AECSEmbed = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  
  return (
    <div className='group w-md cursor-pointer m-8'>
      <div className="mb-4 w-full flex-1 flex-col" key={video.id}>
        <div className="relative shrink-0">
          <div className="aspect-video w-md rounded-lg relative">
            {playing ? (
              <ReactPlayer
                url={video.source_url}
                width="100%"
                height="100%"
                playing={true}
                controls={true}
                className="rounded-lg"
              />
            ) : (
              <>
                <img 
                  src={video.source_metadata.thumbnail_url} 
                  alt={video.source_metadata.title}
                  className="w-full h-full object-cover rounded-lg"
                  onClick={() => setPlaying(true)}
                />
                <div className="absolute inset-0 flex items-center justify-center" onClick={() => setPlaying(true)}>
                  <div className="bg-black bg-opacity-50 rounded-full p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex-1">
          <div className="flex flex-col">
            <div className="mb-1 flex justify-between">
              <h3 className="text-lg font-medium text-white">{video.source_metadata.title}</h3>
            </div>

            <div className="mb-2 text-sm text-gray-500">{video.source_type} • {formatDate(video.source_metadata.published_date)}</div>

            <div className="text-sm text-gray-200 line-clamp-4">{video.source_metadata.description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const YoutubeEmbed = ({ video }) => {
  return (
    <div className='group w-md cursor-pointer m-8'>
      <div className="mb-4 w-full flex-1 flex-col" onClick={() => openUrlInNewTab(video.source_url)} key={video.id}>
        <div className="relative shrink-0">
          <iframe
            className="aspect-video w-md rounded-lg object-cover"
            src={`https://www.youtube.com/embed/${video.source_url.split("?v=")[1]}`}
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
            loading='lazy'></iframe>
        </div>

        <div className="flex-1">
          <div className="flex flex-col">
            <div className="mb-1 flex justify-between">
              <h3 className="text-lg font-medium text-white">{video.source_metadata.title}</h3>
            </div>

            <div className="mb-2 text-sm text-gray-500">{video.source_type} • {formatDate(video.source_metadata.published_date)}</div>

            <div className="text-sm text-gray-200 line-clamp-4">{video.source_metadata.description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
const PrimeVideoEmbed = ({ video }) => {
  return (
    <div className='group w-md cursor-pointer m-8'>
      <div className="mb-4 w-full flex-1 flex-col" onClick={() => openUrlInNewTab(video.source_url)} key={video.id}>
        <div className="relative shrink-0">
          <div className="aspect-video w-md rounded-lg relative">
            <img 
              src={video.source_metadata.thumbnail_url} 
              alt={video.source_metadata.title}
              className="w-full h-full object-cover rounded-lg"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black bg-opacity-50 rounded-full p-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="flex flex-col">
            <div className="mb-1 flex justify-between">
              <h3 className="text-lg font-medium text-white">{video.source_metadata.title}</h3>
            </div>

            <div className="mb-2 text-sm text-gray-500">{video.source_type} • {formatDate(video.source_metadata.published_date)}</div>

            <div className="text-sm text-gray-200 line-clamp-4">{video.source_metadata.description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GalleryItemComponent = (props) => {
  return (
    <div
      key={props.image.id}
      className={`group cursor-pointer w-1/6 ${(props.galleryView && props.selectedItemIndex === props.index) ? 'border-blue-300 border-2 shadow-lg transform scale-105' : ''}`}
      onClick={() => props.onClick(props.index)}
    >
      <div className='w-full overflow-hidden p-2 transition-shadow duration-200 hover:shadow-md'>
        <div className="h-48 w-full overflow-hidden rounded-lg">
          <img src={props.image.url} alt={props.image.source_metadata.title} class="h-full w-full object-contain transition-transform duration-300" loading='lazy' />
        </div>
        <div className="p-2" onClick={() => openUrlInNewTab(props.image.attribution_url)}>
          <p className="mt-1 truncate text-xs text-gray-400">{props.image.attribution_url ? (new URL(props.image.attribution_url).host.replace('www.', '')) : props.image.attribution_text}</p>
          <h3 className="truncate text-sm font-normal text-white hover:underline">{props.image.source_metadata.title || props.image.source_metadata.caption}</h3>
        </div>
      </div>
    </div>
  )
}

const QuerySuggestions = (props) => {
  const suggestions = suggestedQueries().map(sq => sq.query);

  return (
    <div className="mx-20 mt-8 p-4">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Search className="w-5 h-5" />
        Try searching for:
      </h2>

      <div className="flex gap-3">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => props.onSuggestionClick(suggestion)}
            className="flex-shrink-0 px-4 py-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg transition-colors duration-200 text-gray-700 hover:text-blue-700"
          >
            <div className="flex items-center gap-3 whitespace-nowrap">
              <Search className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{suggestion}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const EditableTextToggleDisplay = (props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
   const [text, setText] = useState(props.answer);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    props.handleLLMAnswerChange(text);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  return (
    <div className="max-w-4xl m-4">
      <div className="mb-6">
        <button
          onClick={toggleVisibility}
          className="flex items-center gap-1 px-2 pr-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {isVisible ? (
            <>
              <ChevronUp size={20} />
              Hide Grounded Answer
            </>
          ) : (
            <>
              <ChevronDown size={20} />
              Show Grounded Answer
            </>
          )}
        </button>
      </div>

      {isVisible && (
        <>
          {isEditing && (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                <Save size={14} />
                Save
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
              >
                <X size={14} />
                Cancel
              </button>
            </div>
          )}

          <div className="prose prose-gray max-w-none">
            {isEditing ? (
              <textarea
                value={text}
                onChange={handleTextChange}
                className="w-full h-80 p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-400 leading-relaxed"
                autoFocus
              />
            ) : (
              <div
                className="text-gray-300 leading-relaxed space-y-4 cursor-pointer p-2 rounded transition-colors"
                onDoubleClick={handleDoubleClick}
                title="Double-click to edit"
              >
                {text.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-base">
                    <ReactMarkdown children={paragraph} />
                  </p>
                ))}
                
                {/* Display subqueries if provided */}
                {props.subqueries && props.subqueries.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Subqueries used:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {props.subqueries.map((subquery, index) => (
                        <li key={index} className="text-sm text-gray-300">
                          {subquery}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {!isEditing && (
                  <div className="absolute top-2 right-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Edit3 size={16} />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              {!isEditing && (
                <span className="text-xs text-gray-400">Double-click to edit</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function App(props) {
  let currentStage = getStage() || 'DEV';
  const [user, setUser] = useState(null);
  const [stage, setStage] = useState(currentStage);
  const [region, setRegion] = useState(getRegion(currentStage) || 'us-west-2');
  const [serviceClient, setServiceClient] = useState(null);
  const [llmAnswerGenerationClient, setLLMAnswerGenerationClient] = useState(null);
  const [issueReportingClient, setIssueReportingClient] = useState(null);
  const [textToAkgEntityLinkingClient, setTextToAkgEntityLinkingClient] = useState(null);
  const [searchTermQueryParam, setSearchTermQueryParam] = useState('');
  const [useGroundingQueryParam, setUseGroundingQueryParam] = useState(true);
  const [currentTabQueryParam, setCurrentTabQueryParam] = useState('Images');
  const [useQueryIntelligenceQueryParam, setUseQueryIntelligenceQueryParam] = useState(false);
  const [isMMIRUIBetaUser, setIsMMIRUIBetaUser] = useState(false);

  function createAmplifyListener() {
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
          console.debug("In cognito hosted ui: ", data);
          setUser({ user: data });
          setIsMMIRUIBetaUser(isPartOfBetaUserGroup(data));
          redirectToRememberedPath();
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          console.debug('Sign in failure', data);
          break;
        default:
          console.debug("Event didn't match any of the expected events: ", event);
      }
    });
  }

  async function doCognitoAuth() {
    //fetch the user credentials if midway has already authenticated the user.
    try {
      const user = await Auth.currentAuthenticatedUser();
      setUser({ user: user });
      setIsMMIRUIBetaUser(isPartOfBetaUserGroup(user));
      console.debug('Authenticated user ', user.getUsername());
      redirectToRememberedPath();
    } catch (error) {
      console.debug('Not signed in. Error: ', error);
      //if couldn't fetch credentials, sign in the user by federate:
      await Auth.federatedSignIn({ customProvider: "FederateOIDC" });
    }
  }

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Get search parameters from URL
    const termParam = urlParams.get('q') || '';
    const useGroundingParam = urlParams.get('enableGrounding') === 'false' ? false : true;
    const useQueryIntelligenceParam = urlParams.get('enableQueryIntelligence') === 'true' ? true : false;
    const currentTab = urlParams.get('tab') || 'Images';

    // Update state with URL parameters
    setSearchTermQueryParam(termParam);
    setUseGroundingQueryParam(useGroundingParam);
    setUseQueryIntelligenceQueryParam(useQueryIntelligenceParam);
    setCurrentTabQueryParam(currentTab);

  }, []);

  useEffect(() => {
    setServiceClient(new TracerServiceClient(apiHost[stage][region]));
    setLLMAnswerGenerationClient(new LLMAnswerGenerationClient(groundingGenerationApiHost[stage][region]));
    if (isProdNA(stage, region)) {
      setIssueReportingClient(new IssueReportingClient(mmirUIIssueReportingApiHost[stage][region]));
    }
    if (stage === 'DEV' || stage === 'BETA' || isProdNA(stage, region)) {
      setTextToAkgEntityLinkingClient(new TextToAkgEntityLinkingClient(textToAkgEntityLinkingApiHost[stage][region]));
    }
  }, [stage, region]);

  useEffect(() => {
    initializeAmplify(stage, region);
    createAmplifyListener();
    const doAuth = async () => {
      await doCognitoAuth();
    }
    doAuth();
  }, [stage, region]);

  function TabItem({ label, active = false, icon = null, onClick, disabled = false, tooltip = null }) {
    const handleClick = () => {
      if (!disabled && onClick) {
        onClick();
      }
    };

    return (
      <div
        className={`px-2 py-1 border-b-2 transition-colors relative group
          ${disabled 
            ? 'cursor-not-allowed text-gray-500 border-transparent' 
            : `cursor-pointer hover:text-white ${active ? 'border-blue-500 text-blue-400' : 'border-transparent hover:border-gray-600'}`
          }`}
        onClick={handleClick}
        title={tooltip}
      >
        {icon ? icon : label}
        {tooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
            {tooltip}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    );
  }

  const MultimodalSearch = (props) => {
    const [searchQuery, setSearchQuery] = useState(searchTermQueryParam);
    const [images, setImages] = useState([]);
    const [filteredImages, setFilteredImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [filteredVideos, setFilteredVideos] = useState([]);
    const [mmirRequestId, setMMIRRequestId] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [galleryView, setGalleryView] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [showImageGallery, setShowImageGallery] = useState(true);
    const [showVideoGallery, setShowVideoGallery] = useState(false);
    const [llmAnswer, setLLMAnswer] = useState("");
    const [finalQuery, setFinalQuery] = useState("");
    const [dataFetchFailed, setDataFetchFailed] = useState(false);
    const [activeTab, setActiveTab] = useState(currentTabQueryParam);
    const [useGrounding, setUseGrounding] = useState(useGroundingQueryParam);
    const [useQueryIntelligence, setUseQueryIntelligence] = useState(useQueryIntelligenceQueryParam);
    const [queryIntelligenceSubqueries, setQueryIntelligenceSubqueries] = useState([]);
    const [currentStatusText, setCurrentStatusText] = useState("");
    const [errorMessage, setErrorMessage] = useState("Something went wrong, please try again in some time");
    const [showFilters, setShowFilters] = useState(false);
    const [showToolsTray, setShowToolsTray] = useState(false);
    const [filters, setFilters] = useState({
      profile: DEFAULT_IMAGE_PROFILE,
      locale: DEFAULT_IMAGE_LOCALE,
      confidenceBin: 'Confidence',
      size: 'Size',
      sourceType: 'Source type',
      imageType: 'Image type',
      time: 'Any time'
    });
    const [videoFilters, setVideoFilters] = useState({
      profile: DEFAULT_VIDEO_PROFILE
    });
    const [toolsTrayPosition, setToolsTrayPosition] = useState({ top: 0, left: 0 });
    
    // Ref for the settings button to calculate tray position
    const settingsButtonRef = useRef(null);
    const [wordBoundaries, setWordBoundaries] = useState([]);
    const [expandedEntities, setExpandedEntities] = useState(new Set());

    const filterOptions = {
      profile: IMAGE_PROFILES,
      locale: IMAGE_LOCALES,
      confidenceBin: ['All', ...CONFIDENCE_BINS],
      size: ['All', 'Large', 'Medium', 'Small'],
      sourceType: ['All', 'Wikipedia', 'Getty', 'IMDB', 'Web'],
      imageType: ['All', 'JPEG', 'PNG'],
      time: ['Any time', 'Past 24 hours', 'Past week', 'Past month', 'Past year']
    };

    const videoFilterOptions = {
      profile: VIDEO_PROFILES
    };
    
    
    const enableQueryIntelligence = () => {
      setUseQueryIntelligence(true);
      // Automatically enable grounding when query intelligence is enabled
      setUseGrounding(true);
    }

    const scrollToEntity = (entityIndex) => {
      const entityElement = document.getElementById(`entity-${entityIndex}`);
      if (entityElement) {
        entityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Expand the entity if it's not already expanded
        if (!expandedEntities.has(entityIndex)) {
          toggleEntity(entityIndex);
        }
      }
    };

    const toggleEntity = (entityIndex) => {
      const newExpanded = new Set(expandedEntities);
      if (newExpanded.has(entityIndex)) {
        newExpanded.delete(entityIndex);
      } else {
        newExpanded.add(entityIndex);
      }
      setExpandedEntities(newExpanded);
    };

    const toggleGrounding = () => {
      if (useGrounding && useQueryIntelligence) {
        // Disabling grounding, also turn off query intelligence if it's on
        setUseQueryIntelligence(false);
      }
      setUseGrounding(!useGrounding);
    };

    const toggleQueryIntelligence = () => {
      if (!useQueryIntelligence) {
        // Enabling query intelligence, use centralized function
        enableQueryIntelligence();
      } else {
        // Disabling query intelligence
        setUseQueryIntelligence(false);
      }
    };

    const toggleFilters = () => {
      setShowFilters(!showFilters);
    };

    const toggleToolsTray = () => {
      if (!showToolsTray && settingsButtonRef.current) {
        // Calculate position relative to the settings button
        const rect = settingsButtonRef.current.getBoundingClientRect();
        const trayWidth = 320; // 80 * 4 (w-80 in Tailwind)
        
        // Calculate left position, ensuring tray doesn't go off-screen
        let leftPosition = rect.left + (rect.width / 2) - (trayWidth / 2);
        const rightEdge = leftPosition + trayWidth;
        const windowWidth = window.innerWidth;
        
        // Adjust if tray would go off the right edge
        if (rightEdge > windowWidth - 10) {
          leftPosition = windowWidth - trayWidth - 10;
        }
        
        // Adjust if tray would go off the left edge
        if (leftPosition < 10) {
          leftPosition = 10;
        }
        
        setToolsTrayPosition({
          top: rect.bottom + 8, // 8px gap below the button
          left: leftPosition
        });
      }
      setShowToolsTray(!showToolsTray);
    };

    const suggestedQueryClicked = (suggestedQuery) => {
      setSearchQuery(suggestedQuery);
      setFinalQuery(suggestedQuery);
    }

    const updateURLParams = (query, useGrounding = false, currentTab = 'Images', useQueryIntelligence = false) => {
      const urlParams = new URLSearchParams();
      urlParams.set('enableGrounding', useGrounding);
      urlParams.set('enableQueryIntelligence', useQueryIntelligence);
      urlParams.set('tab', currentTab);
      if (query) urlParams.set('q', query);
      // Update URL without refreshing the page
      const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.pushState({}, '', newUrl);
    };

    const handleReportIssueClick = (issueData) => {
      let data = {
        'reportedByAlias': props.loggedInUser.username.split('_')[1],
        'issueType': `${issueData.issueType} ${issueData.description}`,
        'requestId': mmirRequestId,
        'searchUrl': window.location.href,
        'imageUrl': selectedItem.url,
        'imageMetadata': JSON.stringify(selectedItem.source_metadata)
      }
      console.log(JSON.stringify(data));
      issueReportingClient.createTicket(data);
    }

    const getEntityInfoForCaptionText = useCallback((caption) => {
      async function fetchData() {
        return await textToAkgEntityLinkingClient.getEntities(caption);
      }
      return fetchData();
    }, []);

    // Track if initial search has been performed
    const initialSearchRef = useRef(false);

    // Distinct from initialSearchRef: this stays false for the whole mount pass
    // because it is only set by the effect declared at the end of this component.
    // The change-driven effects below must guard on THIS, not on initialSearchRef:
    // React runs mount effects in declaration order within one commit, so the
    // moment the initial-search effect sets initialSearchRef.current = true, every
    // later effect guarding on it fired a duplicate search on the same page load.
    const hasMountedRef = useRef(false);

    // Single useEffect for initial search
    useEffect(() => {
      if (!initialSearchRef.current && searchQuery) {
        performSearch(searchQuery);
        initialSearchRef.current = true;
      }
    }, [finalQuery]);

    useEffect(() => {
      setWordBoundaries([]);
    }, [selectedItem]);

    // Show the gallery for the active tab, including on initial load: a link
    // opened with tab=Videos has to show the video gallery too. Only the search
    // waits for a real tab change, because the initial-search effect above has
    // already searched on load.
    useEffect(() => {
      chooseGalleryType(activeTab === "Videos" ? "VIDEOS" : "IMAGES");
      if (hasMountedRef.current) {
        performSearch(searchQuery.trim());
      }
    }, [activeTab]);

    // Handle grounding changes without triggering search on initial load
    useEffect(() => {
      if (hasMountedRef.current) {
        setLLMAnswer("");
        setImages([]);
        setVideos([]);
        performSearch(searchQuery.trim());
      }
    }, [useGrounding]);

    // Handle click outside to close tools tray
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (showToolsTray && !event.target.closest('.tools-tray') && !event.target.closest('.tools-button')) {
          setShowToolsTray(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showToolsTray]);

    // Handle window resize to recalculate tray position
    useEffect(() => {
      const handleResize = () => {
        if (showToolsTray && settingsButtonRef.current) {
          const rect = settingsButtonRef.current.getBoundingClientRect();
          const trayWidth = 320; // 80 * 4 (w-80 in Tailwind)
          
          // Calculate left position, ensuring tray doesn't go off-screen
          let leftPosition = rect.left + (rect.width / 2) - (trayWidth / 2);
          const rightEdge = leftPosition + trayWidth;
          const windowWidth = window.innerWidth;
          
          // Adjust if tray would go off the right edge
          if (rightEdge > windowWidth - 10) {
            leftPosition = windowWidth - trayWidth - 10;
          }
          
          // Adjust if tray would go off the left edge
          if (leftPosition < 10) {
            leftPosition = 10;
          }
          
          setToolsTrayPosition({
            top: rect.bottom + 8,
            left: leftPosition
          });
        }
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [showToolsTray]);

    useEffect(() => {
      if (!images?.length) return;

      setGalleryView(false);

      let filteredImages = [...images];
      if (filters.imageType !== 'All' && filters.imageType !== 'Image type') {
        filteredImages = filteredImages.filter((image) => image.image_format.toLowerCase() === filters.imageType.toLowerCase());
      }
      // confidence_bin is a response field, so this narrows what was already fetched
      // rather than re-searching, unlike profile and locale.
      filteredImages = filteredImages.filter((image) => matchesConfidenceBin(image, filters.confidenceBin));
      if (filters.size !== 'All' && filters.size !== 'Size') {
        if (filters.size == 'Small') {
          filteredImages = filteredImages.filter((image) => (image.width < 300 || image.height < 300));
        } else if (filters.size == 'Medium') {
          filteredImages = filteredImages.filter((image) => ((image.width >= 300 && image.width < 1000) || (image.height >= 300 && image.height < 1000)));
        } else if (filters.size == 'Large') {
          filteredImages = filteredImages.filter((image) => (image.width >= 1000 || image.height >= 3000));
        }
      }
      if (filters.sourceType !== 'All' && filters.sourceType !== 'Source type') {
        let selectedSourceType = filters.sourceType;
        if (selectedSourceType == 'Web') {
          selectedSourceType = 'Opengraph';
        }
        if (selectedSourceType === 'Getty') {
          filteredImages = filteredImages.filter((image) => (image.source_text.toLowerCase() === selectedSourceType.toLowerCase() || image.source_text.toLowerCase() === 'getty images'));
        } else {
          filteredImages = filteredImages.filter((image) => image.source_text.toLowerCase() === selectedSourceType.toLowerCase());
        }
      }

      if (filters.time !== 'Any time' && filters.time !== 'Time') {
        let targetTime = 0;
        const now = Date.now();

        if (filters.time === 'Past 24 hours') {
          targetTime = now - (24 * 60 * 60 * 1000); // 24 hours in milliseconds
        } else if (filters.time === 'Past week') {
          targetTime = now - (7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
        } else if (filters.time === 'Past month') {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          targetTime = oneMonthAgo.getTime();
        } else if (filters.time === 'Past year') {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          targetTime = oneYearAgo.getTime();
        }
        filteredImages = filteredImages
          .filter(image => image.source_metadata.creation_date !== undefined)
          .filter(image => new Date(getImageCreationDate(
            image.source_text, image.source_metadata.creation_date,
            image.source_metadata.last_updated_date, image.source_metadata.acquired_at_date,
            image.source_metadata.published_at_date)).getTime() > targetTime);
      }

      setFilteredImages(filteredImages);

    }, [filters]);

    useEffect(() => {
      if (activeTab === 'Videos' && searchQuery && hasMountedRef.current) {
        performSearch(searchQuery.trim());
      }
    }, [videoFilters.profile]);

    useEffect(() => {
      if (activeTab === 'Images' && searchQuery && hasMountedRef.current) {
        performSearch(searchQuery.trim());
      }
    }, [filters.profile]);

    // Locale changes what the service retrieves, so unlike size/sourceType/imageType
    // it cannot be applied to results already in hand and has to re-search.
    useEffect(() => {
      if (activeTab === 'Images' && searchQuery && hasMountedRef.current) {
        performSearch(searchQuery.trim());
      }
    }, [filters.locale]);

    // Declared after every effect that guards on hasMountedRef so the flag is
    // still false while those run their mount pass. Keep it last.
    useEffect(() => {
      hasMountedRef.current = true;
    }, []);

    useEffect(() => {
      setFilteredVideos(videos || []);
    }, [videos]);


    const fetchLLMAnswer = (query_text) => {
      async function fetchData() {
        setCurrentStatusText("Grounding your query and generating answer using Nova");
        let llmAnswer = '';
        try {
          llmAnswer = await llmAnswerGenerationClient.getLLMAnswer(query_text);
        } catch (error) {
          setCurrentStatusText("")
          console.log(error);
        }
        return llmAnswer;
      }

      let filteredSuggestedQuery = suggestedQueries().filter(sq => {return sq.query === query_text});
      if (filteredSuggestedQuery.length === 1) {
        return Promise.resolve(filteredSuggestedQuery[0].answer);
      } else {
        return fetchData();
      }
    }

    const fetchImages = (query_text, llmAnswer) => {
      async function fetchData() {
        try {
          let images_request_body = getDiscoverImagesV2RequestBody(query_text, "100", useQueryIntelligence, filters.profile, filters.locale);
          setCurrentStatusText(`Fetching images for ${searchQuery} ${useGrounding ? 'using query and answer' : ''}${useQueryIntelligence ? ' with Query Intelligence' : ''}`);
          
          if (llmAnswer) {
            images_request_body = getDiscoverImagesV2RequestWithGroundingBody(query_text, llmAnswer, "100", useQueryIntelligence, filters.profile, filters.locale);
          }
          
          let images_response = await serviceClient.discoverImagesV2(images_request_body);
          
          if (images_response.statusCode === 451) {
            setErrorMessage(SENSITIVE_CONTENT_POLICY_ERROR_MESSAGE);
            throw new Error(images_response.message);
          }
          if (images_response.statusCode !== 200) {
            throw new Error(images_response.message);
          }
          
          setMMIRRequestId(images_response?.response_metadata?.amd_request_id);
          
          // If query intelligence is enabled, extract subqueries from response
          if (useQueryIntelligence && images_response.query_intelligence) {
            setQueryIntelligenceSubqueries(images_response.query_intelligence.subqueries)
            if (images_response.query_intelligence.failure_reason) {
              setErrorMessage(images_response.query_intelligence.failure_reason);
              throw new Error(images_response.query_intelligence.failure_reason);
            }
          }
          
          return images_response.images;
        } catch (error) {
          setCurrentStatusText("");
          console.log(error);
        }
        return [];
      }
      return fetchData();
    }

    const fetchVideos = (query_text, llmAnswer) => {
      async function fetchData() {
        try {
          const profile = videoFilters.profile || DEFAULT_VIDEO_PROFILE;
          let videos_request_body = llmAnswer
            ? getDiscoverVideosRequestWithGroundingBody(query_text, llmAnswer, profile)
            : getDiscoverVideosRequestBody(query_text, profile);

          setCurrentStatusText(`Fetching videos for ${searchQuery} ${useGrounding ? 'using query and answer' : ''}`);

          let videos_response = await serviceClient.discoverImagesV2(videos_request_body);
          if (videos_response.statusCode === 451) {
            setErrorMessage(SENSITIVE_CONTENT_POLICY_ERROR_MESSAGE);
            throw new Error(videos_response.message);
          }
          if (videos_response.statusCode !== 200) {
            throw new Error(videos_response.message);
          }
          setMMIRRequestId(videos_response?.response_metadata?.amd_request_id);
          return videos_response.videos;
        } catch (error) {
          setCurrentStatusText("");
          console.log(error);
        }
        return [];
      }
      return fetchData();
    }

    const performSearch = (queryText, llmAnswer='') => {
      // Determine effective tab - switch to Images if query intelligence is enabled and currently on Videos
      let effectiveTab = activeTab;
      if (useQueryIntelligence && activeTab === 'Videos') {
        effectiveTab = 'Images';
        setActiveTab('Images');
      }
      
      function fetchData(llm_answer) {
        if (effectiveTab === "Videos") {
          fetchVideos(queryText, llm_answer).then(videos => {
            setVideos(videos);
            setFilteredVideos(videos);
            setIsSearching(false);
            setDataFetchFailed(false);
            if (!videos?.length) {
              setDataFetchFailed(true);
            }
          });
        } else {
          fetchImages(queryText, llm_answer).then(images => {
            setImages(images);
            setFilteredImages(images);
            setIsSearching(false);
            setDataFetchFailed(false);
            if (!images?.length) {
              setDataFetchFailed(true);
            }
          });
        }
      }
      if (!queryText) {
        setIsSearching(false);
        return;
      }
      updateURLParams(queryText, useGrounding, activeTab, useQueryIntelligence);
      setFinalQuery(queryText);
      setLLMAnswer(llmAnswer);
      setQueryIntelligenceSubqueries([]); // Clear previous subqueries
      setIsSearching(true);
      setGalleryView(false); // Reset to grid view when searching
      setSelectedItem(null); // Clear selected image
      if (useGrounding && !llmAnswer) {
        fetchLLMAnswer(queryText).then(answer => {
          setLLMAnswer(answer);
          fetchData(answer);
        });
      } else {
        fetchData(llmAnswer);
      }
    }

    const handleLLMAnswerChange = (newLLMAnswer) => {
      performSearch(searchQuery.trim(), newLLMAnswer);
    };

    const handleSearch = e => {
      e.preventDefault();
      performSearch(searchQuery.trim());
    };

    // Handle search when pressing Enter in the input field
    const handleKeyPress = e => {
      if (e.key === 'Enter') {
        handleSearch(e);
      }
    };

    const openImageGallery = index => {
      setCurrentItemIndex(index);
      showImageGallery  ? (images.length === filteredImages.length ? setSelectedItem(images[index]):  setSelectedItem(filteredImages[index])): setSelectedItem(videos[index]);
      setGalleryView(true);
    };

    const closeGallery = () => {
      setGalleryView(false);
      setSelectedItem(null);
    };

    const chooseGalleryType = (gallery_type) => {
      if (gallery_type === "VIDEOS") {
        setShowImageGallery(false);
        setShowVideoGallery(true);
      } else {
        setShowImageGallery(true);
        setShowVideoGallery(false);
      }
    }

    const navigateGallery = direction => {
      const newIndex =
        (currentItemIndex + direction + images.length) % images.length;
      setCurrentItemIndex(newIndex);
      showImageGallery ? setSelectedItem(images[newIndex]) : setSelectedItem(videos[newIndex]);
    };

    // Handle keyboard navigation
    const handleKeyDown = e => {
      if (!galleryView) return;

      if (e.key === 'Escape') {
        closeGallery();
      } else if (e.key === 'ArrowRight') {
        navigateGallery(1);
      } else if (e.key === 'ArrowLeft') {
        navigateGallery(-1);
      }
    };

    // Add keyboard event listener
    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [galleryView, currentItemIndex]);

    return (
      <div className='flex flex-row min-h-screen bg-gray-800 text-white-200 font-sans'>
        <CollapsibleSidebar />
        {/* Header */}

        <div className="flex flex-col w-full overflow-x-hidden overscroll-x-none">
          <header className="sticky top-0 z-10 bg-gray-800 p-3 flex items-center shadow-md">
            <div className="flex w-full items-center my-4 mx-20">
              <div className="cursor-pointer">
                <h1 className='text-2xl font-bold text-blue-500'>
                  <a href="/home">MMIR</a>
                </h1>
              </div>

              <div className="relative flex-grow max-w-4xl mx-4">
                <div className={`flex items-center bg-gray-700 rounded-full px-2 py-2`}>
                  <div className="group relative">
                    <button 
                      ref={settingsButtonRef}
                      type="button" 
                      onClick={toggleToolsTray} 
                      className="tools-button relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-600 hover:bg-gray-500 transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
                    >
                      <Settings size={16} className="text-gray-300" />
                    </button>

                    <div className="absolute bottom-full left-1/2 mb-2 hidden w-max -translate-x-1/2 rounded-lg bg-black px-2 py-1 text-xs text-white group-hover:block">
                      Tools
                      <div className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 border-t-4 border-r-4 border-l-4 border-t-black border-r-transparent border-l-transparent"></div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder='search for images and videos'
                    className="bg-transparent border-none outline-none text-white w-full px-4"
                  />
                  <button onClick={handleSearch} className="mx-1 text-gray-400 hover:text-gray-200 transition-colors">
                    <Search size={24} />
                  </button>
                </div>
              </div>
            </div>
          </header>
          
          {(images?.length > 0 || videos?.length > 0) &&
            <nav className="bg-gray-800 text-gray-300 px-4 pb-2 border-b border-gray-700 sticky top-16 z-10">
              <div className="flex space-x-5 text-sm overflow-x-auto no-scrollbar mx-40">
                {['Images', 'Videos'].map((tab) => (
                  <TabItem
                    key={tab}
                    label={tab}
                    active={activeTab === tab}
                    onClick={() => setActiveTab(tab)}
                    disabled={tab === 'Videos' && useQueryIntelligence}
                    tooltip={tab === 'Videos' && useQueryIntelligence ? "Query intelligence does not support videos" : null}
                  />
                ))}
                <div className="flex-grow"></div>
                <TabItem
                  key='Tools'
                  label='Tools'
                  active={false}
                  onClick={() => toggleFilters()}
                />
              </div>
            </nav>
          }

          {showFilters && activeTab === 'Images' && <ImageFilterComponent filters={filters} filterOptions={filterOptions} setFilters={setFilters}/>}
          {showFilters && activeTab === 'Videos' && <VideoFilterComponent filters={videoFilters} filterOptions={videoFilterOptions} setFilters={setVideoFilters}/>}


          {/* Main content */}
          <main className='flex flex-col overflow-y-hidden h-screen'>
            <div className='flex-none'>
              {isSearching ? (
                <div className='flex justify-center text-center py-12'>
                  <button disabled type="button" class="text-white bg-blue-700 dark:text-gray-900 dark:bg-blue-70 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 inline-flex items-center">
                    <svg aria-hidden="true" role="status" class="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                    </svg>
                    {currentStatusText}
                  </button>
                </div>
              ) : null}
              {!isSearching && ((useGrounding && llmAnswer) || images?.length > 0 || videos?.length > 0) && (
                <>
                  {useGrounding && llmAnswer && (
                    <EditableTextToggleDisplay 
                      answer={llmAnswer} 
                      handleLLMAnswerChange={handleLLMAnswerChange} 
                      subqueries={useQueryIntelligence ? queryIntelligenceSubqueries : []}
                    />
                  )}
                </>
              )}
              {!isSearching && !useGrounding && useQueryIntelligence && llmAnswer && (
                <>
                    <EditableTextToggleDisplay 
                      answer={llmAnswer} 
                      handleLLMAnswerChange={() => {}} 
                      subqueries={queryIntelligenceSubqueries}
                    />
                </>
              )}
            </div>
            <div className='flex flex-row h-screen'>
              {!isSearching &&
                <>
                  {showImageGallery && images?.length > 0 &&
                    (
                      <>
                        <div className={`overflow-y-auto h-screen flex flex-col pb-36 ${(galleryView && showImageGallery && selectedItem) ? 'w-3/4' : 'w-full'}`}>
                          <div className='flex flex-wrap px-4 py-4 w-full'>
                            {filteredImages.length === images.length ? images.slice(0, 50).map((image, index) => (
                              <GalleryItemComponent onClick={openImageGallery} image={image} index={index} selectedItemIndex={currentItemIndex} galleryView={galleryView} />
                            )) : filteredImages.slice(0, 50).map((image, index) => (
                              <GalleryItemComponent onClick={openImageGallery} image={image} index={index} selectedItemIndex={currentItemIndex} galleryView={galleryView} />
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                  {showVideoGallery && videos?.length > 0 &&
                    <div className={`overflow-y-auto h-screen flex flex-col pb-36 ${(galleryView && showImageGallery && selectedItem) ? 'w-3/4' : 'w-full'}`}>
                      <div className='ml-8 flex flex-wrap px-4 py-4 w-full'>
                        {filteredVideos.map((video, index) => (
                          video.source_type === "AECS" ? (
                            <AECSEmbed video={video} />
                          ) : video.source_type === "PRIME_VIDEO" ? (
                            <PrimeVideoEmbed video={video} />
                          ) : (
                            <YoutubeEmbed video={video} />
                          )
                        ))}
                      </div>
                    </div>}
                  {!videos?.length && !images?.length &&
                    <div className='overflow-y-auto h-screen flex flex-col w-full'>
                      {dataFetchFailed ?
                        <div className='text-center py-16 w-full'>
                          <div className='text-6xl mb-4'>⚠️</div>
                          <h2 className='text-xl font-medium text-gray-400 mb-1'>
                            {errorMessage}
                          </h2>
                        </div>
                        : (
                          <div className="bg-black/50 bg-blend-overlay h-screen w-full bg-[url(/home-bg.png)] bg-opacity-50 bg-no-repeat bg-cover">
                            <QuerySuggestions onSuggestionClick={suggestedQueryClicked} />
                          </div>
                        )}
                    </div>}
                </>}
              {/* Full-size image modal */}
              {!isSearching && galleryView && showImageGallery && selectedItem && (
                <div
                  className='overflow-y-auto pb-36 flex items-start justify-center w-1/4'
                  onClick={closeGallery}
                >
                  <div
                    className='w-full p-4'
                    onClick={e => e.stopPropagation()}
                  >
                    <div className='relative'>
                      <div className='flex mb-4'>
                        <div className='flex-1 text-gray-200 px-4 py-2 ' onClick={() => openUrlInNewTab(props.image.attribution_url)}>
                          <h3 className='text-lg'>{selectedItem.source_metadata.title}</h3>
                        </div>

                        {window.location.hostname === 'mmir.agi.amazon.dev' && <SearchResultIssueReporter onReport={handleReportIssueClick}/>}
                        <button
                          onClick={() => navigateGallery(-1)}
                          className='flex-none hover:text-gray-400 rounded-full p-2 text-white'
                        >
                          <svg
                            className='w-6 h-6'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigateGallery(1)}
                          className='flex-none hover:text-gray-400 rounded-full p-2 text-white'
                        >
                          <svg
                            className='w-6 h-6'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' />
                          </svg>
                        </button>

                        <button
                          onClick={closeGallery}
                          className='text-white hover:text-gray-400'
                        >
                          <svg
                            className='w-6 h-6'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                          </svg>
                        </button>
                      </div>

                      <div className='p-4 shadow-lg flex justify-center'>
                        <img
                          src={selectedItem.url}
                          alt={selectedItem.alt}
                          className='max-h-96 object-contain'
                        />
                      </div>

                      <div className='mt-4 text-gray-200 p-3 rounded shadow'>
                        <div className='flex justify-between items-center'>
                          <div>
                            {textToAkgEntityLinkingClient ? <TextWithEntities
                              text={selectedItem.source_metadata.caption}
                              getEntityInfo={getEntityInfoForCaptionText}
                              wordBoundaries={wordBoundaries}
                              setWordBoundaries={setWordBoundaries}
                              scrollToEntity={scrollToEntity}
                            /> : <h4 className='font-medium'>{selectedItem.source_metadata.caption}</h4>}
                          </div>
                          <div className='flex space-x-3'>
                            <button className='p-2 text-blue-500 hover:text-blue-500'>
                              <svg
                                className='w-5 h-5'
                                fill='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <a href={selectedItem.url} download="image">
                                  <path d='M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z' />
                                </a>
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2 mt-4">

                          {selectedItem.height && selectedItem.width && (
                            <div className="flex items-center space-x-3">
                              <Hash className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-400">Dimensions:</span>
                              <span className="text-sm font-medium">{selectedItem.width + "x" + selectedItem.height}</span>
                            </div>
                          )}

                          <div className="flex items-center space-x-3">
                            <Globe className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-400">Format:</span>
                            <span className="text-sm font-medium">{selectedItem.image_format}</span>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-400">Created:</span>
                            <span className="text-sm font-medium">{formatDate(selectedItem.source_metadata.creation_date)}</span>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-400">Ingested:</span>
                            <span className="text-sm font-medium">{formatDate(selectedItem.source_metadata.ingestion_date)}</span>
                          </div>

                          {selectedItem.attribution_url && (
                            <div className="flex items-center space-x-3">
                              <Globe className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-400">Source:</span>
                              <span className="text-sm font-medium">{selectedItem.attribution_url ? (new URL(selectedItem.attribution_url).host.replace('www.', '')) : selectedItem.attribution_text}</span>
                            </div>
                          )}

                          {selectedItem.sub_query_idx != null && (
                            <div className="flex items-center space-x-3">
                              <Brain className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-400">Query:</span>
                              <span className="text-sm font-medium">
                                {queryIntelligenceSubqueries[selectedItem.sub_query_idx]}
                                </span>
                            </div>
                          )}

                          {textToAkgEntityLinkingClient && wordBoundaries?.length !== 0 && 
                          <div className="flex-row items-center">
                              <div className="flex items-center space-x-3">
                                <Network className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-400">Entities</span>
                              </div>
                              <EntityInfoList
                                text={selectedItem.source_metadata.caption}
                                wordBoundaries={wordBoundaries}
                                toggleEntity={toggleEntity}
                                expandedEntities={expandedEntities}
                              />
                            </div>}

                          {selectedItem.attribution_url && (
                            <div className="mt-6 pt-4 border-t border-gray-200">
                              <a
                                href={selectedItem.attribution_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-600 transition-colors"
                              >
                                <Globe className="w-4 h-4" />
                                <span className="text-sm font-medium">View Original Source</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
        
        {/* Portal-style Tools Tray - positioned absolutely above everything */}
        {showToolsTray && (
          <div 
            className="tools-tray fixed z-[100] w-80 bg-gray-800 border border-gray-600 rounded-lg shadow-xl"
            style={{
              top: `${toolsTrayPosition.top}px`,
              left: `${toolsTrayPosition.left}px`
            }}
          >
            <div className="p-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3">
                    <Anchor size={16} className="text-blue-400" />
                    <span className="text-white text-sm">Use Grounding</span>
                  </div>
                  <button 
                    type="button" 
                    onClick={toggleGrounding} 
                    className={`relative inline-flex h-5 w-9 items-center rounded-full ${useGrounding ? 'bg-blue-600' : 'bg-gray-500'} transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none`}
                  >
                    <span className={`inline-block h-3 w-3 ${useGrounding ? 'translate-x-5' : 'translate-x-1'} rounded-full bg-white transform transition duration-200 ease-in-out`} />
                  </button>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3">
                    <Brain size={16} className="text-blue-400" />
                    <span className="text-white text-sm">Query Intelligence</span>
                  </div>
                  <button 
                    type="button" 
                    onClick={toggleQueryIntelligence} 
                    className={`relative inline-flex h-5 w-9 items-center rounded-full ${useQueryIntelligence ? 'bg-blue-600' : 'bg-gray-500'} transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none`}
                  >
                    <span className={`inline-block h-3 w-3 ${useQueryIntelligence ? 'translate-x-5' : 'translate-x-1'} rounded-full bg-white transform transition duration-200 ease-in-out`} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Arrow pointing up to the button */}
            <div 
              className="absolute w-4 h-4 bg-gray-800 border-l border-t border-gray-600 rotate-45"
              style={{
                top: '-3px',
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            ></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {user ? <MultimodalSearch loggedInUser={user.user}/> : <p>authenticating...</p>}
    </>
  );
}
